import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FaGlobe, FaLinkedin } from "react-icons/fa";
import circleicon from "../../src/assets/circle.png"

interface Company {
  _id: string;
  name: string;
  logo?: string;
  industry: string;
  country: string;
  teamSize: string;
  fundingAmount?: string;
  inceptionYear: string;
  website?: string;
  linkedin?: string;
  description: string;
  impact: string[];
  sdg: string[];
}

const CompanyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Description");


  const sdgGoals = [
    "No Poverty",
    "Zero Hunger",
    "Good Health and Well-being",
    "Quality Education",
    "Gender Equality",
    "Clean Water and Sanitation",
    "Affordable and Clean Energy",
    "Decent Work and Economic Growth",
    "Industry, Innovation, and Infrastructure",
    "Reduced Inequalities",
    "Sustainable Cities and Communities",
    "Responsible Consumption and Production",
    "Climate Action",
    "Life Below Water",
    "Life on Land",
    "Peace, Justice, and Strong Institutions",
    "Partnerships for the Goals",
  ];
// Extract SDG numbers from database or match by name
const companySdgNumbers = company
  ? company.sdg.map((sdg) => {
      let match = sdg.match(/Goal (\d+)/); // Case 1: "Goal X: Name"
      if (match) return parseInt(match[1], 10);

      match = sdg.match(/^(\d+)\.\s/); // Case 2: "1. Affordable and Clean Energy"
      if (match) return parseInt(match[1], 10);

      // Case 3: Match by SDG name if no number is found
      const sdgIndex = sdgGoals.findIndex((goal) =>
        goal.toLowerCase() === sdg.replace(/^\d+\.\s*/, "").toLowerCase().trim()
      );
      return sdgIndex !== -1 ? sdgIndex + 1 : null;
    }).filter(Boolean) // Remove null values
  : [];
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await fetch(`https://d2q95isnao7wof.cloudfront.net/api/companies/getallcompanies/${id}`);
        const data = await response.json();
        setCompany(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching company:", error);
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 ">
        <p>Loading...</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p>Company not found</p>
      </div>
    );
  }

  return (
    <div className="lg:px-4 lg:py-10 p-4 bg-gray-100 min-h-screen">
      <div className="bg-gray-100 w-full h-full lg:max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="flex lg:items-center space-x-10 lg:mx-auto">
  {company.logo && (
    <img
      src={company.logo}
      alt={`${company.name} logo`}
      className="lg:w-48 lg:h-48 w-24 h-24 p-4 object-contain rounded-md bg-white"
      onError={(e) =>
        (e.currentTarget.src =
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgAB/ntTVW8AAAAASUVORK5CYII=")
      }
    />
  )}
  <div>
    <h1 className="ml-1 lg:ml-0 text-base lg:text-3xl font-bold text-gray-800">
      {company.name}
    </h1>
    <div className="lg:flex lg:flex-col flex gap-1 mt-2 lg:ml-0 ml-2">
      {company.linkedin && (
        <a
          href={company.linkedin}
          target="_blank"
          rel="noreferrer"
          className="text-[#939393] flex items-center hover:underline"
        >
          <FaLinkedin className="mr-1" />{" "}
          <div className="hidden lg:block"> {company.linkedin}</div>
        </a>
      )}
      {company.website && (
        <a
          href={company.website}
          target="_blank"
          rel="noreferrer"
          className="text-[#939393] flex items-center hover:underline"
        >
          <FaGlobe className="mr-1 " />{" "}
          <div className="hidden lg:block"> {company.website}</div>
        </a>
      )}
    </div>

    <div className="mt-4 grid grid-cols-3 gap-4 text-sm text-gray-600 bg-white rounded-lg lg:px-6 lg:py-4 p-2">
      <div>
        <p className="font-medium">Funding Amount</p>
        <p className="font-bold">{company.fundingAmount || "N/A"}</p>
      </div>
      <div>
        <p className="font-medium">Inception (Year)</p>
        <p className="font-bold">{company.inceptionYear}</p>
      </div>
      <div>
        <p className="lg:hidden font-medium">
          Team <br /> Size
        </p>
        <p className="hidden lg:block font-medium">Team Size</p>
        <p className="font-bold">{company.teamSize}</p>
      </div>
    </div>
  </div>
</div>

        

     {/* Tabs */}
<div className="lg:flex lg:w-[40%] flex w-full py-10">
  <button
    onClick={() => setActiveTab("Description")}
    className={`${
      activeTab === "Description" ? "bg-[#F9B509] rounded-lg" : "bg-[#766F6F12]"
    } w-full text-black font-semibold px-6 py-3  rounded-l-lg runded-r-md focus:outline-none`}>
    Description
  </button>
  <button
    onClick={() => setActiveTab("Impact Created")}
    className={`${
      activeTab === "Impact Created" ? "bg-[#F9B509] rounded-lg" : "bg-[#766F6F12]"
    } w-full text-black font-semibold px-6 py-3  rounded-r-lg focus:outline-none`}>
    Impact Created
  </button>
</div>

{/* Tab Content */}
{activeTab === "Description" ? (
  <div>
    <p className="text-gray-700 leading-relaxed">
      {company.description || "No description available."}
    </p>
  </div>
) : (
  <div>
    <ul className="list-disc pl-6 text-gray-700">
      {company.impact && company.impact.length > 0 ? (
        company.impact.map((item, index) => <li key={index}>{item}</li>)
      ) : (
        <li>No impact data available</li>
      )}
    </ul>
  </div>
)}


       {/* SDG Section */}

<h2 className=" mt-10 text-center lg:text-start text-base lg:text-xl font-semibold text-white bg-[#002B41] p-4 rounded-lg">
  UNDP Sustainable Development Goals
</h2>



<div className="lg:grid lg:grid-cols-2 grid-cols-1 gap-4 mt-6">
  {sdgGoals.map((goal, index) => {
    const goalNumber = index + 1; // SDG numbers start from 1
    const isMatched = companySdgNumbers.includes(goalNumber);

    return (
      <div
        key={index}
        className={`flex items-center px-1 py-4 lg:p-4 rounded-lg shadow mb-2 ${
           isMatched ? "bg-gray-50 border-gray-200" : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="relative w-4 h-4 mr-3">
          {/* Base Circle Icon */}
          <div
            className={`w-full h-full rounded-full border-2 border-blue-800 ${
              isMatched ? "bg-yellow-400" : "bg-white"
            }`}
          ></div>
        </div>

        {/* SDG Text */}
        <span className="lg:hidden font-bold text-xs">Goal {goalNumber}:</span>
        <span className="lg:hidden text-xs ml-1">{goal}</span>
        <span className="hidden lg:block font-bold">Goal {goalNumber}:</span>
        <span className="hidden lg:block ml-1">{goal}</span>
      </div>
    );
  })}
</div>

      </div>
    </div>
  );
};

export default CompanyPage;
